/**
 * A fila de avisos passageiros — o "toast" do sistema.
 *
 * ## O que vira aviso, e o que NÃO vira
 *
 * A regra não é estética, é de permanência:
 *
 *   SUCESSO vira aviso. "Perfil salvo", "3 itens entraram no estoque" — a
 *   pessoa precisa saber que deu certo, e depois não precisa mais. Uma faixa
 *   verde permanente dentro do cartão ocupa espaço para sempre por causa de
 *   uma informação que valia dois segundos, e empurra o formulário para baixo
 *   toda vez que alguém salva.
 *
 *   ERRO DE CAMPO NÃO vira aviso. "CNPJ inválido" pertence AO CAMPO, com
 *   `aria-invalid` e a mensagem embaixo. Um toast que some em quatro segundos
 *   levaria embora a única pista de onde está o problema, num formulário de
 *   dezoito campos.
 *
 *   ERRO DE OPERAÇÃO fica onde está: a faixa vermelha dentro do formulário.
 *   Ela descreve algo que ainda está errado e continua errado depois de quatro
 *   segundos — sumir sozinha seria esconder o problema.
 *
 * Ou seja: some sozinho o que já terminou; fica o que ainda pede ação.
 *
 * ## Por que um módulo, e não um contexto de React
 *
 * Uma fila em módulo é alcançável de qualquer lugar sem provider e sem prop
 * atravessando cinco camadas — inclusive de fora de componente, que é onde
 * fica boa parte do tratamento de erro. `useSyncExternalStore` liga a fila ao
 * React sem cópia de estado.
 */
import { useSyncExternalStore } from 'react'

export type TomAviso = 'sucesso' | 'erro' | 'info'

export type Aviso = {
  id: number
  tom: TomAviso
  texto: string
}

/** 4 s: o tempo de ler seis palavras e voltar ao que se estava fazendo. */
const DURACAO_MS = 4000

let fila: Aviso[] = []
let proximoId = 1
const inscritos = new Set<() => void>()

function avisar() {
  for (const notificar of inscritos) notificar()
}

/**
 * Enfileira um aviso.
 *
 * `erro` aqui é para a falha que a pessoa não pode corrigir no formulário —
 * "o servidor não respondeu". Erro de validação continua no campo.
 */
export function mostrarAviso(texto: string, tom: TomAviso = 'sucesso') {
  const aviso: Aviso = { id: proximoId++, tom, texto }
  fila = [...fila, aviso]
  avisar()

  // O erro fica mais tempo: quem errou precisa ler o que aconteceu, e a
  // mensagem costuma ser mais longa que a de sucesso.
  const espera = tom === 'erro' ? DURACAO_MS * 1.75 : DURACAO_MS
  setTimeout(() => dispensarAviso(aviso.id), espera)
}

export function dispensarAviso(id: number) {
  const antes = fila.length
  fila = fila.filter((aviso) => aviso.id !== id)
  if (fila.length !== antes) avisar()
}

function inscrever(notificar: () => void) {
  inscritos.add(notificar)
  return () => {
    inscritos.delete(notificar)
  }
}

function lerFila() {
  return fila
}

/** Lê a fila no React. Usado só pela `BarraDeAvisos`. */
export function useAvisos() {
  return useSyncExternalStore(inscrever, lerFila, lerFila)
}

/** Limpa a fila. Existe para o teste não vazar aviso de um caso para o outro. */
export function limparAvisos() {
  fila = []
  proximoId = 1
  avisar()
}
